import { FC } from 'react'

import Image from 'next/image'

import {
  footerBgPattern,
  phoneHand100,
  uberIcon,
  womanPhoneEllipse,
} from '@ui/assets'
import { useSignUpHref } from '@ui/layout/signUpHref'
import { marketingUrl } from '@ui/lib/urls'

export const FeaturesGrid: FC = () => {
  const signUpHref = useSignUpHref()
  return (
    <section className="bg-white py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          {/* Instant Cash Advances */}
          <div className="relative flex h-[620px] flex-col justify-end overflow-hidden rounded-[50px] bg-[#006fff] p-12 text-white">
            <div className="absolute right-0 top-[-208px] h-[540px] w-[410px]">
              <Image
                src={womanPhoneEllipse}
                width={410}
                height={540}
                alt=""
                className="rotate-[30deg]"
              />
            </div>

            <div className="absolute left-8 top-[203px] z-10 inline-flex items-center gap-3 rounded-2xl bg-white px-3 py-3 shadow-lg md:top-[78px]">
              <Image src={uberIcon} width={62} height={62} alt="" />
              <p className="font-display pr-4 text-base text-[#0a2d4a]">
                Get 3% cash back
              </p>
            </div>

            <div className="relative z-10">
              <h3 className="font-display mb-4 text-5xl leading-none tracking-tight">
                Rewards &<br />
                <span className="text-[#29bfed]">Cashback</span>
              </h3>
              <p className="text-lg leading-relaxed">
                Earn cash back on every purchase and unlock exclusive rewards
                with our partner merchants.
              </p>
            </div>
          </div>

          {/* Instant Cash Advances */}
          <div className="relative flex h-[620px] flex-col overflow-hidden rounded-[50px] bg-[#daf1fa] p-12 pt-8">
            <div className="mb-12 text-center">
              <h3 className="font-display mb-4 text-4xl leading-none tracking-tight text-[#0a2d4a] md:text-6xl lg:text-[56px]">
                Instant Cash <span className="text-[#29bfed]">Advances</span>
              </h3>
              <p className="mx-auto max-w-md text-lg text-[#0a2d4a]">
                Get up to $100 deposited into your account instantly, with no
                credit check required.
              </p>
            </div>

            <div className="relative">
              {/* Shadow cards stacked below */}
              <div className="absolute left-2 right-2 top-[2px] z-0 h-[220px] rounded-2xl bg-white/70" />
              <div className="absolute left-4 right-4 top-[6px] z-0 h-[226px] rounded-2xl bg-white/50" />

              {/* Main card on top */}
              <div className="relative z-10 rounded-2xl bg-white p-6">
                <div className="mb-4">
                  <span className="text-sm font-semibold text-[#0a3754]">
                    ATM Instant Cash
                  </span>
                </div>
                <p className="font-display mb-6 text-5xl text-[#0a3754]">
                  $100
                </p>
                <div className="flex gap-3">
                  <a
                    href={marketingUrl('/download')}
                    className="font-display flex-1 rounded-full bg-[#29bfed] py-4 text-center text-sm text-[#0a3754] md:text-base"
                  >
                    Take Advance
                  </a>
                  <a
                    href={marketingUrl('/download')}
                    className="font-display flex-1 rounded-full bg-[#0a3754] py-4 text-center text-sm text-white md:text-base"
                  >
                    Repay Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Track Spending Section */}
        <div className="relative mt-5 overflow-hidden rounded-[50px] bg-[#0a2d4a] text-white">
          {/* Background blob pattern */}
          <div className="absolute inset-0 z-0">
            <Image src={footerBgPattern} fill alt="" className="object-cover" />
          </div>

          {/* Content - Desktop: side by side, Mobile: stacked */}
          <div className="relative z-10 flex flex-col p-8 pb-0 sm:p-12 sm:pb-0 md:flex-row md:items-end md:justify-between md:p-16 md:pb-0">
            {/* Text content */}
            <div className="flex flex-col items-center text-center md:max-w-[500px] md:items-start md:pb-16 md:text-left">
              <h3 className="font-display mb-6 text-5xl leading-none tracking-tight sm:text-6xl lg:text-[68px]">
                Track spending
                <br />
                & boost your
                <br />
                <span className="text-[#2cceff]">savings</span>
              </h3>
              <p className="mb-8 max-w-md text-base leading-relaxed sm:text-lg">
                Monitor your finances in real-time, automatically set budgets,
                and watch your money grow with automated tools that help you
                stay on track.
              </p>

              <a
                href={signUpHref}
                className="inline-flex items-center gap-4 rounded-full bg-white py-1 pl-6 pr-1 font-bold text-[#0a2d4a] transition-all hover:bg-gray-50"
              >
                <span className="text-[17px] tracking-tight">Get Started</span>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#29bfed]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="#0a2d4a"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </a>
            </div>

            {/* Phone in hand */}
            <div className="relative z-10 mx-auto mb-[-150px] mt-8 flex w-full max-w-sm justify-center md:mb-[-80px] md:mt-0 md:max-w-md">
              <Image
                src={phoneHand100}
                width={531}
                height={684}
                alt="Phone in hand"
                className="h-auto w-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
